import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Sidebar } from '../../components/Sidebar';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ListChecks, Plus, Users, Trash2, ArrowUpRight, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const BrandShortlists = () => {
  const [shortlists, setShortlists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openCreate, setOpenCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '' });
  const [creating, setCreating] = useState(false);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/shortlists`, { withCredentials: true });
      setShortlists(Array.isArray(data) ? data : []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load shortlists');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    const name = form.name.trim();
    if (!name) {
      toast.error('Shortlist name is required');
      return;
    }
    setCreating(true);
    try {
      await axios.post(
        `${API_URL}/api/shortlists`,
        { name, description: form.description.trim() || null },
        { withCredentials: true }
      );
      toast.success('Shortlist created');
      setOpenCreate(false);
      setForm({ name: '', description: '' });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create');
    } finally {
      setCreating(false);
    }
  };

  const remove = async (id, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this shortlist? The creators stay on the platform.')) return;
    try {
      await axios.delete(`${API_URL}/api/shortlists/${id}`, { withCredentials: true });
      toast.success('Deleted');
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete');
    }
  };

  return (
    <div className="flex min-h-screen">
      <Sidebar />
      <div className="flex-1 p-8 bg-white dark:bg-[#0A0B0D]" data-testid="brand-shortlists-page">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
            <div>
              <h1 className="text-4xl font-bold tracking-tight text-[#0F0F0F] dark:text-white mb-2 flex items-center gap-3">
                <ListChecks className="text-[#FF451A]" size={32} />
                Shortlists
              </h1>
              <p className="text-[#6B6B6B] dark:text-zinc-400">Save creators into named collections. Brief them as a batch.</p>
            </div>
            <Button
              onClick={() => setOpenCreate(true)}
              className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17] shadow-[0_4px_20px_rgba(255,69,26,0.3)] flex items-center gap-2"
              data-testid="create-shortlist-button"
            >
              <Plus size={16} /> New Shortlist
            </Button>
          </div>

          {loading ? (
            <div className="py-20 text-center"><Loader2 className="inline-block animate-spin text-[#FF451A]" size={28} /></div>
          ) : shortlists.length === 0 ? (
            <div className="bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-16 text-center" data-testid="shortlists-empty-state">
              <ListChecks className="mx-auto text-[#6B6B6B] dark:text-zinc-500 mb-3" size={44} />
              <h3 className="text-lg font-semibold text-[#0F0F0F] dark:text-white mb-1">No shortlists yet</h3>
              <p className="text-sm text-[#6B6B6B] dark:text-zinc-400 mb-5">Create one and start saving creators from Discovery.</p>
              <Button onClick={() => setOpenCreate(true)} className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 py-2.5 font-medium hover:bg-[#E63E17]">
                <Plus size={14} className="mr-1.5" /> Create your first shortlist
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="shortlists-grid">
              {shortlists.map((s) => (
                <Link
                  key={s.id}
                  to={`/brand/shortlists/${s.id}`}
                  className="group bg-white dark:bg-[#0A0B0D] border border-black/[0.08] dark:border-white/5 rounded-2xl p-5 hover:border-[#FF451A]/30 hover:-translate-y-[2px] transition-all flex flex-col gap-3"
                  data-testid="shortlist-card"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-[#0F0F0F] dark:text-white truncate">{s.name}</div>
                      {s.description && <div className="text-xs text-[#6B6B6B] dark:text-zinc-400 mt-0.5 line-clamp-2">{s.description}</div>}
                    </div>
                    <button
                      onClick={(e) => remove(s.id, e)}
                      className="text-[#6B6B6B] dark:text-zinc-500 hover:text-[#FF3B30] p-1 transition-colors"
                      data-testid="shortlist-delete-button"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between mt-auto">
                    <div className="flex items-center gap-1.5 text-sm text-zinc-300">
                      <Users size={13} className="text-[#FF451A]" /> <span className="font-semibold">{s.creator_count}</span>
                      <span className="text-[#6B6B6B] dark:text-zinc-500">creator{s.creator_count === 1 ? '' : 's'}</span>
                    </div>
                    <div className="text-[10px] text-[#6B6B6B] dark:text-zinc-500 font-mono">
                      {(() => { try { return `updated ${formatDistanceToNow(new Date(s.updated_at), { addSuffix: true })}`; } catch { return ''; } })()}
                    </div>
                  </div>
                  <div className="text-xs text-[#FF451A] inline-flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    Open workspace <ArrowUpRight size={11} />
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </div>

      <Dialog open={openCreate} onOpenChange={setOpenCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New shortlist</DialogTitle>
            <DialogDescription className="text-sm text-[#6B6B6B] dark:text-zinc-400">
              Name your collection and start saving creators from Discovery.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={create} className="space-y-3">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Name</label>
              <Input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Summer Beauty Campaign"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="shortlist-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider block mb-1 text-[#0F0F0F] dark:text-white">Description (optional)</label>
              <Input
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder="Mumbai-based skincare creators with 50K+ followers"
                className="rounded-xl bg-white dark:bg-[#1A1D24] border border-black/[0.12] dark:border-white/[0.08] text-[#0F0F0F] dark:text-white placeholder-[#A1A1AA] dark:placeholder-zinc-500"
                data-testid="shortlist-description-input"
              />
            </div>
            <div className="flex gap-2 pt-2">
              <Button
                type="submit"
                disabled={creating}
                className="bg-[#FF451A] text-[#0F0F0F] dark:text-white rounded-full px-5 hover:bg-[#E63E17] flex-1 disabled:opacity-40"
                data-testid="shortlist-create-submit"
              >
                {creating ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Plus size={14} className="mr-1.5" />}
                Create
              </Button>
              <Button type="button" onClick={() => setOpenCreate(false)} className="border border-black/[0.10] dark:border-white/10 bg-transparent text-[#0F0F0F] dark:text-white hover:bg-white/5 rounded-full px-5">
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
